import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { ChevronDown, Play, Pause, AlertTriangle } from "lucide-react";

interface TTSWarning {
  status: string;
  reason?: string;
  at: number;
}

interface TranscriptRow {
  id: string;
  speaker_label: string | null;
  text: string;
  is_final: boolean;
  started_at: string;
  audio_base64: string | null;
}

const FORMAT_MIME: Record<string, string> = {
  mp3: "audio/mpeg",
  mpeg: "audio/mpeg",
  wav: "audio/wav",
  ogg: "audio/ogg",
  webm: "audio/webm",
};

function base64ToArrayBuffer(b64: string): ArrayBuffer {
  const bin = atob(b64);
  const buf = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) buf[i] = bin.charCodeAt(i);
  return buf.buffer;
}

async function logPlaybackEvent(
  sessionId: string,
  transcriptId: string,
  event: "play" | "pause" | "complete" | "error" | "decode_error" | "replay",
  extra?: { error_text?: string; format?: string; duration_ms?: number },
) {
  try {
    await supabase.functions.invoke("jarvis-pipeline", {
      body: {
        action: "playback_event",
        session_id: sessionId,
        transcript_id: transcriptId,
        event,
        user_agent: navigator.userAgent.slice(0, 200),
        ...extra,
      },
    });
  } catch {
    /* fire-and-forget */
  }
}

/**
 * Stream de transcrição ao vivo via Supabase Realtime.
 * - Smart auto-scroll
 * - Replay de áudio (▶/⏸) por turn quando audio_base64 está disponível
 * - Telemetria fire-and-forget de eventos de playback
 */
export function JarvisTranscriptStream({
  sessionId,
  className,
  maxItems = 30,
  ttsWarning,
}: {
  sessionId: string | null;
  className?: string;
  maxItems?: number;
  ttsWarning?: TTSWarning | null;
}) {
  const [items, setItems] = useState<TranscriptRow[]>([]);
  const [hasNew, setHasNew] = useState(false);
  const [playingId, setPlayingId] = useState<string | null>(null);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const isNearBottomRef = useRef(true);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const currentSourceRef = useRef<AudioBufferSourceNode | null>(null);

  const checkNearBottom = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return true;
    return el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  }, []);

  const handleScroll = useCallback(() => {
    isNearBottomRef.current = checkNearBottom();
    if (isNearBottomRef.current) setHasNew(false);
  }, [checkNearBottom]);

  const scrollToBottom = useCallback((smooth = true) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: smooth ? "smooth" : "auto" });
    isNearBottomRef.current = true;
    setHasNew(false);
  }, []);

  useEffect(() => {
    if (!sessionId) {
      setItems([]);
      return;
    }

    let cancelled = false;
    void supabase
      .from("jarvis_transcripts")
      .select("id, speaker_label, text, is_final, started_at, audio_base64")
      .eq("session_id", sessionId)
      .order("started_at", { ascending: false })
      .limit(maxItems)
      .then(({ data }) => {
        if (!cancelled && data) {
          setItems(data.reverse() as TranscriptRow[]);
          requestAnimationFrame(() => scrollToBottom(false));
        }
      });

    const channel = supabase
      .channel(`jarvis-transcripts-${sessionId}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "jarvis_transcripts",
          filter: `session_id=eq.${sessionId}`,
        },
        (payload) => {
          const row = payload.new as TranscriptRow;
          setItems((prev) => [...prev.slice(-(maxItems - 1)), row]);
        },
      )
      .subscribe();

    return () => {
      cancelled = true;
      void supabase.removeChannel(channel);
    };
  }, [sessionId, maxItems, scrollToBottom]);

  useEffect(() => {
    if (items.length === 0) return;
    if (isNearBottomRef.current) {
      requestAnimationFrame(() => scrollToBottom(true));
    } else {
      setHasNew(true);
    }
  }, [items, scrollToBottom]);

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      try { currentSourceRef.current?.stop(); } catch { /* noop */ }
      currentSourceRef.current = null;
      const ctx = audioCtxRef.current;
      if (ctx && ctx.state !== "closed") ctx.close().catch(() => {});
      audioCtxRef.current = null;
    };
  }, []);

  const stopPlayback = useCallback(() => {
    const src = currentSourceRef.current;
    if (src) {
      try { src.onended = null; src.stop(); src.disconnect(); } catch { /* noop */ }
      currentSourceRef.current = null;
    }
    setPlayingId(null);
  }, []);

  const handleReplay = useCallback(async (row: TranscriptRow) => {
    if (!sessionId || !row.audio_base64) return;
    if (playingId === row.id) {
      stopPlayback();
      void logPlaybackEvent(sessionId, row.id, "pause");
      return;
    }
    stopPlayback();
    try {
      if (!audioCtxRef.current) {
        const Ctor: typeof AudioContext =
          (window.AudioContext as typeof AudioContext) ||
          ((window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext as typeof AudioContext);
        if (!Ctor) throw new Error("AudioContext unsupported");
        audioCtxRef.current = new Ctor();
      }
      const ctx = audioCtxRef.current;
      if (ctx.state === "suspended") await ctx.resume();
      const arr = base64ToArrayBuffer(row.audio_base64);
      const buf = await ctx.decodeAudioData(arr.slice(0));
      const src = ctx.createBufferSource();
      src.buffer = buf;
      src.connect(ctx.destination);
      currentSourceRef.current = src;
      setPlayingId(row.id);
      void logPlaybackEvent(sessionId, row.id, "replay", { duration_ms: Math.round(buf.duration * 1000) });
      src.onended = () => {
        if (currentSourceRef.current === src) currentSourceRef.current = null;
        setPlayingId((cur) => (cur === row.id ? null : cur));
        void logPlaybackEvent(sessionId, row.id, "complete");
      };
      src.start(0);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      console.warn("[JarvisTranscriptStream] replay failed", msg);
      void logPlaybackEvent(sessionId, row.id, "decode_error", { error_text: msg });
      setPlayingId(null);
    }
  }, [sessionId, playingId, stopPlayback]);

  if (!sessionId) {
    return (
      <div className={cn("text-sm text-muted-foreground italic", className)}>
        Inicie uma sessão para ver a transcrição ao vivo.
      </div>
    );
  }

  return (
    <div className={cn("relative h-full min-h-0 flex flex-col", className)}>
      {ttsWarning && (
        <Alert variant="destructive" className="mb-2">
          <AlertTriangle className="w-4 h-4" />
          <AlertTitle className="text-xs">Síntese de voz indisponível</AlertTitle>
          <AlertDescription className="text-[11px]">
            Respondendo apenas em texto. Motivo: <code>{ttsWarning.reason ?? ttsWarning.status}</code>
          </AlertDescription>
        </Alert>
      )}
      <div
        ref={scrollerRef}
        onScroll={handleScroll}
        className="flex-1 min-h-0 overflow-y-auto max-h-[60vh] pr-2 space-y-3"
      >
        {items.length === 0 ? (
          <div className="text-sm text-muted-foreground italic">
            Aguardando primeira interação…
          </div>
        ) : (
          items.map((row) => {
            const isJarvis = row.speaker_label === "jarvis";
            const canReplay = isJarvis && !!row.audio_base64;
            const isPlaying = playingId === row.id;
            return (
              <div
                key={row.id}
                className={cn(
                  "rounded-lg p-3 text-sm",
                  isJarvis
                    ? "bg-primary/10 border border-primary/20"
                    : "bg-muted/50 border border-border/50",
                )}
              >
                <div className="flex items-center justify-between mb-1 gap-2">
                  <Badge variant={isJarvis ? "default" : "secondary"} className="text-[10px] uppercase tracking-wider">
                    {isJarvis ? "Jarvis" : (row.speaker_label ?? "Você")}
                  </Badge>
                  <div className="flex items-center gap-2">
                    {canReplay && (
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-6 w-6"
                        onClick={() => void handleReplay(row)}
                        title={isPlaying ? "Pausar" : "Reproduzir"}
                      >
                        {isPlaying ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3" />}
                      </Button>
                    )}
                    <span className="text-[10px] text-muted-foreground tabular-nums">
                      {new Date(row.started_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
                    </span>
                  </div>
                </div>
                <div className="text-foreground leading-relaxed">{row.text}</div>
              </div>
            );
          })
        )}
      </div>
      {hasNew && (
        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 z-10">
          <Button size="sm" variant="secondary" className="shadow-md" onClick={() => scrollToBottom(true)}>
            <ChevronDown className="w-3 h-3 mr-1" /> Novas mensagens
          </Button>
        </div>
      )}
    </div>
  );
}
